import { z } from 'zod';
import {
  AuditSchema,
  InstantSchema,
  OrderIdSchema,
  PartyIdSchema,
  PlainDateSchema,
  ShipmentIdSchema,
  TenantScopedSchema,
  TimeWindowSchema,
  UserIdSchema,
} from './primitives.js';
import { HandlingClassSchema } from './primitives.js';

/* ------------------------------------------------------------------ *
 * Logistics
 *
 * A route is a vehicle, a driver and an ordered list of stops for one
 * day. A shipment is what one stop delivers. Orders attach to
 * shipments — plural, because a hotel that ordered chilled and frozen
 * may legitimately get two drops.
 * ------------------------------------------------------------------ */

export const VehicleSchema = AuditSchema.merge(TenantScopedSchema).extend({
  id: z.string().min(1),
  name: z.string().min(1),
  plate: z.string().min(1),
  /** Which handling classes this vehicle can legally carry. A van without a chiller cannot take fresh. */
  handlingClasses: z.array(HandlingClassSchema).min(1),
  capacityKg: z.number().positive().nullable().default(null),
  capacityCrates: z.number().int().positive().nullable().default(null),
  status: z.enum(['available', 'in-use', 'maintenance']).default('available'),
});
export type Vehicle = z.infer<typeof VehicleSchema>;

export const StopStatusSchema = z.enum([
  'pending', 'en-route', 'arrived', 'delivered', 'partial', 'failed', 'skipped',
]);
export type StopStatus = z.infer<typeof StopStatusSchema>;

export const ProofOfDeliverySchema = z.object({
  signedBy: z.string().nullable().default(null),
  signatureUrl: z.string().nullable().default(null),
  photoUrls: z.array(z.string()).default([]),
  /** Where the driver actually was when they marked it delivered. */
  lat: z.number().nullable().default(null),
  lng: z.number().nullable().default(null),
  capturedAt: InstantSchema,
});
export type ProofOfDelivery = z.infer<typeof ProofOfDeliverySchema>;

export const ShipmentSchema = AuditSchema.merge(TenantScopedSchema).extend({
  id: ShipmentIdSchema,
  number: z.string().min(1),
  routeId: z.string().nullable().default(null),
  customerId: PartyIdSchema,
  customerName: z.string().min(1),
  addressId: z.string().nullable().default(null),
  addressLabel: z.string().min(1),
  orderIds: z.array(OrderIdSchema).min(1),

  date: PlainDateSchema,
  window: TimeWindowSchema.nullable().default(null),
  /** Position in the route. Drivers reorder on the fly; the number is not the id. */
  sequence: z.number().int().min(0).default(0),
  status: StopStatusSchema.default('pending'),

  etaAt: InstantSchema.nullable().default(null),
  arrivedAt: InstantSchema.nullable().default(null),
  completedAt: InstantSchema.nullable().default(null),

  crateCount: z.number().int().min(0).default(0),
  /** Returnable crates the driver took back. Deposits reconcile against this. */
  cratesReturned: z.number().int().min(0).default(0),

  proof: ProofOfDeliverySchema.nullable().default(null),
  failureReason: z.string().nullable().default(null),
  note: z.string().nullable().default(null),
});
export type Shipment = z.infer<typeof ShipmentSchema>;

export const RouteSchema = AuditSchema.merge(TenantScopedSchema).extend({
  id: z.string().min(1),
  name: z.string().min(1),
  date: PlainDateSchema,
  vehicleId: z.string().nullable().default(null),
  driverId: UserIdSchema.nullable().default(null),
  /** Third-party carrier when we don't drive it ourselves. */
  carrierPartyId: PartyIdSchema.nullable().default(null),
  status: z.enum(['planning', 'dispatched', 'completed', 'cancelled']).default('planning'),
  departsAt: InstantSchema.nullable().default(null),
  returnedAt: InstantSchema.nullable().default(null),
  shipmentIds: z.array(ShipmentIdSchema).default([]),
  note: z.string().nullable().default(null),
});
export type Route = z.infer<typeof RouteSchema>;
